import React from "react";
import { Clock, Filter, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ExceptionFilters } from "./ExceptionFilters";
import { RuleExceptionFilter } from "../types";

interface ExceptionHeaderProps {
  formattedDate: string;
  loading: boolean;
  ruleExceptionsCount: number;
  hasActiveFilters: boolean;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  filters: RuleExceptionFilter;
  onFilterChange: (filters: Partial<RuleExceptionFilter>) => void;
}

export const ExceptionHeader: React.FC<ExceptionHeaderProps> = ({
  formattedDate,
  loading,
  ruleExceptionsCount,
  hasActiveFilters,
  showFilters,
  setShowFilters,
  filters,
  onFilterChange,
}) => {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <ShieldAlert className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Exceptions</h1>
            <div className="flex items-center space-x-2 text-slate-600 mt-1">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-medium">
                Period: {formattedDate}
              </span>
              {loading && (
                <Badge variant="outline" className="ml-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-2"></div>
                  Loading...
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className="flex items-center space-x-4">
          <div className="text-sm text-slate-600">
            {ruleExceptionsCount} exception{ruleExceptionsCount !== 1 ? 's' : ''}
            {hasActiveFilters && ' (filtered)'}
          </div>
          
          <Popover open={showFilters} onOpenChange={setShowFilters}>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className={`${hasActiveFilters ? 'border-orange-200 bg-orange-50 text-orange-700' : ''}`}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                    {Object.values(filters).filter(Boolean).length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <ExceptionFilters
                filters={filters}
                onFilterChange={onFilterChange}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
}; 